import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Label,
  TextInput,
  ToggleSwitch,
  Tabs,
  TabItem,
  Badge,
  Spinner,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  Modal,
  Select,
} from 'flowbite-react';
import {
  HiCollection,
  HiPlus,
  HiPencil,
  HiTrash,
  HiSearch,
  HiX,
  HiTag,
  HiChevronUp,
  HiChevronDown,
} from 'react-icons/hi';
import {
  descriptorsApi,
  Descriptor,
  DescriptorType,
  DescriptorCreateData,
  DescriptorUpdateData,
  DescriptorTypeCreateData,
  DescriptorTypeUpdateData,
} from '../api/descriptors';
import { getUserErrorMessage } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

export const DescriptorManagementPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Redirect non-superAdmin users
  useEffect(() => {
    if (currentUser && currentUser.role !== 'superAdmin') {
      navigate('/access-denied');
    }
  }, [currentUser, navigate]);

  // --- Data state ---
  const [allDescriptors, setAllDescriptors] = useState<Descriptor[]>([]);
  const [descriptorTypes, setDescriptorTypes] = useState<DescriptorType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // --- Descriptor filters & sorting ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('true'); // 'true', 'false', 'all'
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);

  // --- Modal states ---
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [selectedDescriptor, setSelectedDescriptor] = useState<Descriptor | null>(null);
  const [selectedDescriptorType, setSelectedDescriptorType] = useState<DescriptorType | null>(null);
  const [saving, setSaving] = useState(false);

  // --- Descriptor form ---
  const [formData, setFormData] = useState<DescriptorCreateData>({
    namespace: '',
    codeValue: '',
    shortDescription: '',
    descriptorTypeId: null,
    description: '',
    sortOrder: 0,
    active: true,
  });

  // --- Descriptor Type form ---
  const [typeFormData, setTypeFormData] = useState<DescriptorTypeCreateData>({
    descriptorTypeName: '',
    description: '',
    sortOrder: 0,
    active: true,
  });

  // --- Data loading ---
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [descriptors, types] = await Promise.all([
        descriptorsApi.list({ activeOnly: false, limit: 1000 }),
        descriptorsApi.getTypes(false),
      ]);
      setAllDescriptors(descriptors);
      setDescriptorTypes(types);
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to load descriptors'));
      console.error('Error loading descriptors:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Build a type name lookup ---
  const typeNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    descriptorTypes.forEach(t => {
      map[t.descriptorTypeId] = t.descriptorTypeName;
    });
    return map;
  }, [descriptorTypes]);

  // --- Count descriptors per type ---
  const typeDescriptorCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    allDescriptors.forEach(d => {
      if (d.descriptorTypeId) {
        counts[d.descriptorTypeId] = (counts[d.descriptorTypeId] || 0) + 1;
      }
    });
    return counts;
  }, [allDescriptors]);

  // --- Filtered & sorted descriptors ---
  const filteredDescriptors = useMemo(() => {
    let result = [...allDescriptors];

    // Filter by type
    if (selectedTypeId !== null) {
      result = result.filter(d => d.descriptorTypeId === selectedTypeId);
    }

    // Filter by active status
    if (activeFilter === 'true') {
      result = result.filter(d => d.active);
    } else if (activeFilter === 'false') {
      result = result.filter(d => !d.active);
    }

    // Search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(d =>
        d.codeValue.toLowerCase().includes(term) ||
        d.shortDescription.toLowerCase().includes(term) ||
        (d.namespace && d.namespace.toLowerCase().includes(term))
      );
    }

    // Sort
    if (sortField) {
      result.sort((a, b) => {
        let aVal: string | number | boolean = '';
        let bVal: string | number | boolean = '';

        switch (sortField) {
          case 'type':
            aVal = (a.descriptorTypeId ? typeNameMap[a.descriptorTypeId] : '') || '';
            bVal = (b.descriptorTypeId ? typeNameMap[b.descriptorTypeId] : '') || '';
            break;
          case 'codeValue':
            aVal = a.codeValue;
            bVal = b.codeValue;
            break;
          case 'shortDescription':
            aVal = a.shortDescription;
            bVal = b.shortDescription;
            break;
          case 'active':
            aVal = a.active ? 1 : 0;
            bVal = b.active ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const cmp = aVal.localeCompare(bVal);
          return sortDirection === 'asc' ? cmp : -cmp;
        }
        const diff = (aVal as number) - (bVal as number);
        return sortDirection === 'asc' ? diff : -diff;
      });
    } else {
      // Default sort: by type name, then by shortDescription within each type
      result.sort((a, b) => {
        const aType = (a.descriptorTypeId ? typeNameMap[a.descriptorTypeId] : '') || '';
        const bType = (b.descriptorTypeId ? typeNameMap[b.descriptorTypeId] : '') || '';
        const typeCmp = aType.localeCompare(bType);
        if (typeCmp !== 0) return typeCmp;
        return a.shortDescription.localeCompare(b.shortDescription);
      });
    }

    return result;
  }, [allDescriptors, selectedTypeId, activeFilter, searchTerm, sortField, sortDirection, typeNameMap]);

  // --- Pagination ---
  const totalPages = Math.max(1, Math.ceil(filteredDescriptors.length / PAGE_SIZE));
  const paginatedDescriptors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredDescriptors.slice(start, start + PAGE_SIZE);
  }, [filteredDescriptors, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedTypeId, activeFilter, sortField, sortDirection]);

  // --- Sorting handler ---
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <HiChevronUp className="w-4 h-4 ml-1 inline" />
      : <HiChevronDown className="w-4 h-4 ml-1 inline" />;
  };

  // --- Reset filters ---
  const hasActiveFilters = selectedTypeId !== null || activeFilter !== 'true' || searchTerm !== '' || sortField !== '';

  const resetFilters = () => {
    setSelectedTypeId(null);
    setActiveFilter('true');
    setSearchTerm('');
    setSortField('');
    setSortDirection('asc');
    setCurrentPage(1);
  };

  // --- Auto-generate namespace from type ---
  const generateNamespace = (typeId: number | null | undefined): string => {
    if (!typeId) return '';
    const typeName = typeNameMap[typeId];
    if (!typeName) return '';
    // Convert "Staff Role" -> "StaffRole", "Grade Level" -> "GradeLevel"
    const camel = typeName.replace(/\s+/g, '');
    return `uri://aiu-directory/${camel}Descriptor`;
  };

  // --- Dismiss helpers ---
  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  // ============================================================
  // DESCRIPTOR CRUD
  // ============================================================

  const openCreateDescriptorModal = () => {
    clearMessages();
    setFormData({
      namespace: '',
      codeValue: '',
      shortDescription: '',
      descriptorTypeId: null,
      description: '',
      sortOrder: 0,
      active: true,
    });
    setShowCreateModal(true);
  };

  const openEditDescriptorModal = (descriptor: Descriptor) => {
    clearMessages();
    setSelectedDescriptor(descriptor);
    setFormData({
      namespace: descriptor.namespace || '',
      codeValue: descriptor.codeValue,
      shortDescription: descriptor.shortDescription,
      descriptorTypeId: descriptor.descriptorTypeId ?? null,
      description: '',
      sortOrder: descriptor.sortOrder ?? 0,
      active: descriptor.active,
    });
    // Load full details to get description
    descriptorsApi.getDescriptor(descriptor.descriptorId).then(detail => {
      setFormData(prev => ({
        ...prev,
        description: detail.description || '',
      }));
    }).catch(err => {
      console.error('Error loading descriptor details:', err);
    });
    setShowEditModal(true);
  };

  const handleCreateDescriptor = async () => {
    try {
      setSaving(true);
      clearMessages();
      await descriptorsApi.createDescriptor(formData);
      setShowCreateModal(false);
      setSuccessMessage('Descriptor created successfully');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to create descriptor'));
      console.error('Error creating descriptor:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDescriptor = async () => {
    if (!selectedDescriptor) return;
    try {
      setSaving(true);
      clearMessages();
      const updateData: DescriptorUpdateData = { ...formData };
      await descriptorsApi.updateDescriptor(selectedDescriptor.descriptorId, updateData);
      setShowEditModal(false);
      setSelectedDescriptor(null);
      setSuccessMessage('Descriptor updated successfully');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to update descriptor'));
      console.error('Error updating descriptor:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDescriptor = async (descriptor: Descriptor) => {
    if (descriptor.active) {
      // Deactivate
      if (!confirm(`Are you sure you want to deactivate "${descriptor.shortDescription}"? It can be reactivated later.`)) return;
      try {
        clearMessages();
        await descriptorsApi.updateDescriptor(descriptor.descriptorId, { active: false });
        setSuccessMessage(`"${descriptor.shortDescription}" deactivated`);
        await loadData();
      } catch (err) {
        setError(getUserErrorMessage(err, 'Failed to deactivate descriptor'));
      }
    } else {
      // Permanently delete
      if (!confirm(`Are you sure you want to PERMANENTLY DELETE "${descriptor.shortDescription}"? This cannot be undone.`)) return;
      try {
        clearMessages();
        await descriptorsApi.deleteDescriptor(descriptor.descriptorId);
        setSuccessMessage(`"${descriptor.shortDescription}" deleted permanently`);
        await loadData();
      } catch (err) {
        setError(getUserErrorMessage(err, 'Failed to delete descriptor'));
      }
    }
  };

  // ============================================================
  // DESCRIPTOR TYPE CRUD
  // ============================================================

  const openCreateTypeModal = () => {
    clearMessages();
    setTypeFormData({
      descriptorTypeName: '',
      description: '',
      sortOrder: 0,
      active: true,
    });
    setShowCreateTypeModal(true);
  };

  const openEditTypeModal = (dt: DescriptorType) => {
    clearMessages();
    setSelectedDescriptorType(dt);
    setTypeFormData({
      descriptorTypeName: dt.descriptorTypeName,
      description: dt.description || '',
      sortOrder: dt.sortOrder ?? 0,
      active: dt.active,
    });
    setShowEditTypeModal(true);
  };

  const handleCreateType = async () => {
    try {
      setSaving(true);
      clearMessages();
      await descriptorsApi.createType(typeFormData);
      setShowCreateTypeModal(false);
      setSuccessMessage('Descriptor type created successfully');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to create descriptor type'));
      console.error('Error creating descriptor type:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateType = async () => {
    if (!selectedDescriptorType) return;
    try {
      setSaving(true);
      clearMessages();
      const updateData: DescriptorTypeUpdateData = { ...typeFormData };
      await descriptorsApi.updateType(selectedDescriptorType.descriptorTypeId, updateData);
      setShowEditTypeModal(false);
      setSelectedDescriptorType(null);
      setSuccessMessage('Descriptor type updated successfully');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err, 'Failed to update descriptor type'));
      console.error('Error updating descriptor type:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteType = async (dt: DescriptorType) => {
    const count = typeDescriptorCounts[dt.descriptorTypeId] || 0;
    if (dt.active) {
      if (count > 0) {
        setError(`Cannot deactivate "${dt.descriptorTypeName}" because it has ${count} descriptor(s). Remove or reassign them first.`);
        return;
      }
      if (!confirm(`Are you sure you want to deactivate "${dt.descriptorTypeName}"?`)) return;
      try {
        clearMessages();
        await descriptorsApi.updateType(dt.descriptorTypeId, { active: false });
        setSuccessMessage(`"${dt.descriptorTypeName}" deactivated`);
        await loadData();
      } catch (err) {
        setError(getUserErrorMessage(err, 'Failed to deactivate descriptor type'));
      }
    } else {
      if (!confirm(`Are you sure you want to PERMANENTLY DELETE "${dt.descriptorTypeName}"? This cannot be undone.`)) return;
      try {
        clearMessages();
        await descriptorsApi.deleteType(dt.descriptorTypeId);
        setSuccessMessage(`"${dt.descriptorTypeName}" deleted permanently`);
        await loadData();
      } catch (err) {
        setError(getUserErrorMessage(err, 'Failed to delete descriptor type'));
      }
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (loading && allDescriptors.length === 0) {
    return (
      <div className="p-6 flex justify-center items-center min-h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Descriptor Management</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Manage standardized codes and reference data used throughout the application
        </p>
      </div>

      {/* Messages */}
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Tabs>
        {/* ==================== DESCRIPTORS TAB ==================== */}
        <TabItem title="Descriptors" icon={HiCollection}>
          <div className="space-y-4">
            {/* Controls */}
            <Card>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-64">
                  <Label htmlFor="search">Search descriptors</Label>
                  <TextInput
                    id="search"
                    icon={HiSearch}
                    placeholder="Search by code, description, or namespace..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="min-w-48">
                  <Label htmlFor="typeFilter">Descriptor Type</Label>
                  <Select
                    id="typeFilter"
                    value={selectedTypeId ?? ''}
                    onChange={e => setSelectedTypeId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">All Types</option>
                    {descriptorTypes
                      .slice()
                      .sort((a, b) => a.descriptorTypeName.localeCompare(b.descriptorTypeName))
                      .map(t => (
                        <option key={t.descriptorTypeId} value={t.descriptorTypeId}>
                          {t.descriptorTypeName} ({typeDescriptorCounts[t.descriptorTypeId] || 0})
                        </option>
                      ))}
                  </Select>
                </div>

                <div className="min-w-32">
                  <Label htmlFor="activeFilter">Status</Label>
                  <Select
                    id="activeFilter"
                    value={activeFilter}
                    onChange={e => setActiveFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Select>
                </div>

                <button
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <HiX className="w-4 h-4 mr-2" />
                  Reset All
                </button>

                <button
                  onClick={openCreateDescriptorModal}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <HiPlus className="w-4 h-4 mr-2" />
                  Add Descriptor
                </button>
              </div>
            </Card>

            {/* Results summary + pagination */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {paginatedDescriptors.length} of {filteredDescriptors.length} descriptors
                </p>
                {sortField && (
                  <Badge color="info" size="sm">
                    Sorted by {sortField} ({sortDirection})
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            {/* Descriptors Table */}
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeadCell
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => handleSort('type')}
                      >
                        Type <SortIcon field="type" />
                      </TableHeadCell>
                      <TableHeadCell
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => handleSort('codeValue')}
                      >
                        Code Value <SortIcon field="codeValue" />
                      </TableHeadCell>
                      <TableHeadCell
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => handleSort('shortDescription')}
                      >
                        Description <SortIcon field="shortDescription" />
                      </TableHeadCell>
                      <TableHeadCell
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => handleSort('active')}
                      >
                        Status <SortIcon field="active" />
                      </TableHeadCell>
                      <TableHeadCell>Actions</TableHeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDescriptors.map(descriptor => (
                      <TableRow key={descriptor.descriptorId}>
                        <TableCell>
                          <Badge color="gray" size="sm">
                            {descriptor.descriptorTypeId ? typeNameMap[descriptor.descriptorTypeId] || '—' : '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">
                          {descriptor.codeValue}
                        </TableCell>
                        <TableCell>{descriptor.shortDescription}</TableCell>
                        <TableCell>
                          <Badge color={descriptor.active ? 'green' : 'red'} size="sm">
                            {descriptor.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditDescriptorModal(descriptor)}
                              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                              title="Edit descriptor"
                            >
                              <HiPencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDescriptor(descriptor)}
                              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                              title={descriptor.active ? 'Deactivate descriptor' : 'Permanently delete descriptor'}
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {paginatedDescriptors.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No descriptors found matching your criteria
                </div>
              )}
            </Card>
          </div>
        </TabItem>

        {/* ==================== DESCRIPTOR TYPES TAB ==================== */}
        <TabItem title="Descriptor Types" icon={HiTag}>
          <div className="space-y-4">
            <Card>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Descriptor Types</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Manage categories that group related descriptors
                  </p>
                </div>
                <button
                  onClick={openCreateTypeModal}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  <HiPlus className="w-4 h-4 mr-2" />
                  Add Type
                </button>
              </div>
            </Card>

            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeadCell>Type Name</TableHeadCell>
                      <TableHeadCell>Description</TableHeadCell>
                      <TableHeadCell>Descriptors</TableHeadCell>
                      <TableHeadCell>Sort Order</TableHeadCell>
                      <TableHeadCell>Status</TableHeadCell>
                      <TableHeadCell>Actions</TableHeadCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {descriptorTypes
                      .slice()
                      .sort((a, b) => a.descriptorTypeName.localeCompare(b.descriptorTypeName))
                      .map(dt => (
                        <TableRow key={dt.descriptorTypeId}>
                          <TableCell className="font-medium text-gray-900 dark:text-white">
                            {dt.descriptorTypeName}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                            {dt.description || '—'}
                          </TableCell>
                          <TableCell>
                            <Badge color="blue" size="sm">
                              {typeDescriptorCounts[dt.descriptorTypeId] || 0} descriptors
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                            {dt.sortOrder ?? '—'}
                          </TableCell>
                          <TableCell>
                            <Badge color={dt.active ? 'green' : 'red'} size="sm">
                              {dt.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openEditTypeModal(dt)}
                                className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                                title="Edit type"
                              >
                                <HiPencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteType(dt)}
                                className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                                title={
                                  dt.active && (typeDescriptorCounts[dt.descriptorTypeId] || 0) > 0
                                    ? 'Cannot deactivate — type has descriptors'
                                    : dt.active
                                      ? 'Deactivate type'
                                      : 'Permanently delete type'
                                }
                              >
                                <HiTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>

              {descriptorTypes.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No descriptor types found
                </div>
              )}
            </Card>
          </div>
        </TabItem>
      </Tabs>

      {/* ==================== CREATE DESCRIPTOR MODAL ==================== */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)} dismissible popup>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Descriptor</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-type">Descriptor Type *</Label>
              <Select
                id="create-type"
                value={formData.descriptorTypeId ?? ''}
                onChange={e => {
                  const typeId = e.target.value ? Number(e.target.value) : null;
                  setFormData(prev => ({
                    ...prev,
                    descriptorTypeId: typeId,
                    namespace: generateNamespace(typeId),
                  }));
                }}
                required
              >
                <option value="">Select a type...</option>
                {descriptorTypes
                  .filter(t => t.active)
                  .sort((a, b) => a.descriptorTypeName.localeCompare(b.descriptorTypeName))
                  .map(t => (
                    <option key={t.descriptorTypeId} value={t.descriptorTypeId}>
                      {t.descriptorTypeName}
                    </option>
                  ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="create-codeValue">Code Value *</Label>
              <TextInput
                id="create-codeValue"
                value={formData.codeValue}
                onChange={e => setFormData(prev => ({ ...prev, codeValue: e.target.value }))}
                placeholder="e.g., Superintendent"
                required
              />
            </div>

            <div>
              <Label htmlFor="create-shortDesc">Short Description *</Label>
              <TextInput
                id="create-shortDesc"
                value={formData.shortDescription}
                onChange={e => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                placeholder="Display text"
                required
              />
            </div>

            <div>
              <Label htmlFor="create-description">Description</Label>
              <TextInput
                id="create-description"
                value={formData.description || ''}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detailed description (optional)"
              />
            </div>

            <div>
              <Label htmlFor="create-namespace">Namespace</Label>
              <TextInput
                id="create-namespace"
                value={formData.namespace}
                onChange={e => setFormData(prev => ({ ...prev, namespace: e.target.value }))}
                placeholder="Auto-generated from type"
                color="gray"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-generated from type. Override only if needed.</p>
            </div>

            <div>
              <Label htmlFor="create-sortOrder">Sort Order</Label>
              <TextInput
                id="create-sortOrder"
                type="number"
                value={formData.sortOrder ?? 0}
                onChange={e => setFormData(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <ToggleSwitch
                checked={formData.active ?? true}
                onChange={checked => setFormData(prev => ({ ...prev, active: checked }))}
                id="create-active"
              />
              <Label htmlFor="create-active">Active</Label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleCreateDescriptor}
              disabled={saving || !formData.codeValue || !formData.shortDescription || !formData.descriptorTypeId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating...' : 'Create Descriptor'}
            </button>
            <button
              onClick={() => setShowCreateModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* ==================== EDIT DESCRIPTOR MODAL ==================== */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)} dismissible popup>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Descriptor</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-type">Descriptor Type *</Label>
              <Select
                id="edit-type"
                value={formData.descriptorTypeId ?? ''}
                onChange={e => {
                  const typeId = e.target.value ? Number(e.target.value) : null;
                  setFormData(prev => ({ ...prev, descriptorTypeId: typeId }));
                }}
                required
              >
                {descriptorTypes
                  .sort((a, b) => a.descriptorTypeName.localeCompare(b.descriptorTypeName))
                  .map(t => (
                    <option key={t.descriptorTypeId} value={t.descriptorTypeId}>
                      {t.descriptorTypeName}
                    </option>
                  ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-codeValue">Code Value *</Label>
              <TextInput
                id="edit-codeValue"
                value={formData.codeValue}
                onChange={e => setFormData(prev => ({ ...prev, codeValue: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-shortDesc">Short Description *</Label>
              <TextInput
                id="edit-shortDesc"
                value={formData.shortDescription}
                onChange={e => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-description">Description</Label>
              <TextInput
                id="edit-description"
                value={formData.description || ''}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="edit-namespace">Namespace</Label>
              <TextInput
                id="edit-namespace"
                value={formData.namespace}
                onChange={e => setFormData(prev => ({ ...prev, namespace: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="edit-sortOrder">Sort Order</Label>
              <TextInput
                id="edit-sortOrder"
                type="number"
                value={formData.sortOrder ?? 0}
                onChange={e => setFormData(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <ToggleSwitch
                checked={formData.active ?? true}
                onChange={checked => setFormData(prev => ({ ...prev, active: checked }))}
                id="edit-active"
              />
              <Label htmlFor="edit-active">Active</Label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleUpdateDescriptor}
              disabled={saving || !formData.codeValue || !formData.shortDescription}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Update Descriptor'}
            </button>
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* ==================== CREATE TYPE MODAL ==================== */}
      <Modal show={showCreateTypeModal} onClose={() => setShowCreateTypeModal(false)} dismissible popup>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Descriptor Type</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="create-typeName">Type Name *</Label>
              <TextInput
                id="create-typeName"
                value={typeFormData.descriptorTypeName}
                onChange={e => setTypeFormData(prev => ({ ...prev, descriptorTypeName: e.target.value }))}
                placeholder="e.g., Staff Role"
                required
              />
            </div>

            <div>
              <Label htmlFor="create-typeDesc">Description</Label>
              <TextInput
                id="create-typeDesc"
                value={typeFormData.description || ''}
                onChange={e => setTypeFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description (optional)"
              />
            </div>

            <div>
              <Label htmlFor="create-typeSortOrder">Sort Order</Label>
              <TextInput
                id="create-typeSortOrder"
                type="number"
                value={typeFormData.sortOrder ?? 0}
                onChange={e => setTypeFormData(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <ToggleSwitch
                checked={typeFormData.active ?? true}
                onChange={checked => setTypeFormData(prev => ({ ...prev, active: checked }))}
                id="create-typeActive"
              />
              <Label htmlFor="create-typeActive">Active</Label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleCreateType}
              disabled={saving || !typeFormData.descriptorTypeName}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Creating...' : 'Create Type'}
            </button>
            <button
              onClick={() => setShowCreateTypeModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* ==================== EDIT TYPE MODAL ==================== */}
      <Modal show={showEditTypeModal} onClose={() => setShowEditTypeModal(false)} dismissible popup>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Descriptor Type</h3>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-typeName">Type Name *</Label>
              <TextInput
                id="edit-typeName"
                value={typeFormData.descriptorTypeName}
                onChange={e => setTypeFormData(prev => ({ ...prev, descriptorTypeName: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-typeDesc">Description</Label>
              <TextInput
                id="edit-typeDesc"
                value={typeFormData.description || ''}
                onChange={e => setTypeFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="edit-typeSortOrder">Sort Order</Label>
              <TextInput
                id="edit-typeSortOrder"
                type="number"
                value={typeFormData.sortOrder ?? 0}
                onChange={e => setTypeFormData(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <ToggleSwitch
                checked={typeFormData.active ?? true}
                onChange={checked => setTypeFormData(prev => ({ ...prev, active: checked }))}
                id="edit-typeActive"
              />
              <Label htmlFor="edit-typeActive">Active</Label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleUpdateType}
              disabled={saving || !typeFormData.descriptorTypeName}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Update Type'}
            </button>
            <button
              onClick={() => setShowEditTypeModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
